import React, { useState, ChangeEvent, useRef, ReactElement, useEffect } from 'react';
import { useFormContext, Controller, RegisterOptions } from 'react-hook-form';
import clsx from 'clsx';
import Button from '../controls/Button';
import LocationCapture from './LocationCapture';
import { DeviceLocation, ExtendedDeviceAttributes } from '../constants/telosair-interfaces';

/**
 * FileUploadProps.
 */
interface FileUploadProps {
  selectedDevices: ExtendedDeviceAttributes[];
  onFileChange: (file: File) => Promise<void>;
  newDevicesWithLocation: DeviceLocation;
  setNewDevicesWithLocation: (data: DeviceLocation) => void;
  fileData: string;
  isEditing: boolean;
  validationObject?: RegisterOptions;
  idname: string;
}

/**
 * Custom File Upload component with image preview and sensor tagging component.
 * @param props - FileUploadProps.
 * @returns - Returns a react element for file upload.
 */
export default function FileUpload(props: FileUploadProps): ReactElement {
  const {
    selectedDevices,
    onFileChange,
    newDevicesWithLocation,
    setNewDevicesWithLocation,
    fileData,
    isEditing,
    validationObject,
    idname
  } = props;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(fileData || null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Handles changes in file upload.
   * @param event - The event object representing the file upload input change.
   */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    if (file) {
      onFileChange(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        /**
         * Handle the 'onload' event for the FileReader.
         * @param e - The event object.
         */
        reader.onload = (e) => {
          setImagePreview(e?.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    }
  };

  useEffect(() => {
    setImagePreview(fileData || null);
  }, [fileData]);

  const {
    control,
    formState: { errors }
  } = useFormContext();

  return (
    <>
      <div className="pt-3 mb-2 flex text-gray-500 dark:text-gray-400 text-sm">
        Upload Floor Map
      </div>
      <Controller
        name={idname}
        control={control}
        rules={validationObject}
        render={({ field: { onChange, onBlur, ref } }) => (
          <div
            className={clsx(
              'border rounded-lg p-3 flex items-center',
              { 'border-error-300': errors[idname] },
              { 'border-gray-700': !errors[idname] }
            )}
          >
            <label
              htmlFor={idname}
              className="flex-1 pr-2 text-base font-normal text-gray-300 dark:text-gray-300 placeholder-gray-400"
            >
              {isEditing ? (
                <span>Remote File</span>
              ) : (
                <span>{selectedFile ? selectedFile.name : 'No file selected'}</span>
              )}
            </label>
            <input
              type="file"
              accept=".png"
              className="hidden"
              id={idname}
              ref={(e) => {
                ref(e);
                fileInputRef.current = e;
              }}
              onChange={(e) => {
                onChange(e);
                handleFileChange(e);
              }}
              onBlur={onBlur}
            />
            <Button
              theme="secondary-color"
              size="xs"
              name="Upload"
              onClick={() => fileInputRef?.current?.click()}
            />
          </div>
        )}
      />
      <div
        className={clsx('w-full pt-2 pb-5 text-sm font-normal leading-tight', {
          'text-gray-500': !errors[idname],
          'text-error-500': errors[idname]
        })}
      >
        {errors[idname]
          ? errors[idname]?.message?.toString()
          : 'The file should be of size between 100KB to 5MB, File type .png'}
      </div>

      {selectedDevices.length > 0 && (
        <>
          <div className="text-gray-500 py-2 text-sm font-medium dark:text-gray-400 leading-tight">
            Tag Device Locations
          </div>
          <div className="w-full py-5 text-sm font-normal text-gray-500 dark:text-gray-500 leading-tight">
            Tag device locations by clicking on locations of devices on the floor map you have
            provided
          </div>
          <LocationCapture
            newDevicesWithLocation={newDevicesWithLocation}
            setNewDevicesWithLocation={setNewDevicesWithLocation}
            imageSrc={imagePreview}
            selectedDevices={selectedDevices}
          />
        </>
      )}
    </>
  );
}
